import Chip from "@components/SearchManagement/Chip";
import GradientCover from "@components/common/GradientCover";
import TidbitBullet from "@components/icons/TidbitBullet";
import { ITravelConsultant } from "@components/pages/travel-consultant/components/TCDetailsLayout";
import ImageCloudinary from "@components/skeleton/ImageCloudinary";
import MediaAssets from "@components/utils/MediaAssets";
import { ImageDefault } from "@components/utils/Utils";
import useAppContext from "@hooks/useAppContext";
import dynamic from "next/dynamic";
import { useMemo } from "react";

const Breadcrumb = dynamic(
  async () => await import("@modules/breadcrumb/Breadcrumb"),
  { ssr: false }
);

interface ITCDetailHeader {
  travelConsultant: ITravelConsultant;
}

const MAX_TIDBITS = 4;
const MAX_SPECS_PER_ROW = 4;

const TCDetailHeader = ({ travelConsultant }: ITCDetailHeader): JSX.Element => {
  const { isMobile } = useAppContext();

  const tidbitsComponents = useMemo(() => {
    return (
      <ul className="flex flex-col gap-3">
        {travelConsultant?.tidbits?.slice(0, MAX_TIDBITS).map((tidbit) => (
          <li key={tidbit.id} className="flex flex-row gap-2">
            <TidbitBullet variant={isMobile ? "light" : "dark"} />
            <span className="text-h6 font-medium text-white desktop:text-darkgray">
              {tidbit.text}
            </span>
          </li>
        ))}
      </ul>
    );
  }, [travelConsultant.tidbits, isMobile]);

  const specialtiesComponents = useMemo(() => {
    const specialtyChips = travelConsultant?.specialties?.map((specialty) => (
      <Chip
        key={specialty.id}
        item={{
          title: specialty.name,
        }}
        variant={isMobile ? "white" : "default"}
        onClick={undefined}
      />
    ));

    return (
      <div className="flex flex-col gap-3 desktop:gap-4">
        <span className="text-h5 font-medium text-white desktop:text-black">
          My Specialties
        </span>

        <div className="no-scrollbar block flex max-h-[4rem] flex-wrap gap-x-1 gap-y-2 overflow-hidden desktop:hidden">
          {specialtyChips?.slice(0, 8)}
        </div>

        <div className="no-scrollbar flex hidden flex-col gap-2 overflow-hidden desktop:block">
          <div className="flex flex-row gap-1">
            {specialtyChips?.slice(0, MAX_SPECS_PER_ROW)}
          </div>
          <div className="h-[0.25rem]" />
          <div className="flex flex-row gap-1">
            {specialtyChips?.slice(MAX_SPECS_PER_ROW, MAX_SPECS_PER_ROW * 2)}
          </div>
        </div>
      </div>
    );
  }, [travelConsultant.specialties, isMobile]);

  const backgroundStyle = useMemo(() => {
    if (!isMobile) {
      return {
        backgroundImage: `url(${MediaAssets.getAsset("topo_background")})`,
        backgroundSize: "auto 100%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "35%",
        maxWidth: "100%",
        maxHeight: "100%",
      };
    }
  }, [isMobile]);

  return isMobile ? (
    // MOBILE CARD DESIGN
    <div className="mx-3 mt-4">
      <div className="mb-4 h-[1.1875rem]">
        <Breadcrumb />
      </div>
      <div className={`relative w-full rounded-3xl bg-black`}>
        <div className="relative block h-[19rem] w-full">
          <GradientCover bottomREM={3.5} />
          <div className="absolute top-0 z-[0] h-[22.5rem] w-full">
            <ImageCloudinary
              className="z-[0] rounded-t-3xl"
              src={ImageDefault(travelConsultant.image)}
              alt="w"
              width="360"
              height="360"
              layout="fill"
              objectFit="cover"
              priority={true}
            />
          </div>
        </div>
        <div className="relative flex min-h-[20rem] w-full flex-col justify-between gap-8 px-5 pb-5">
          <div className="z-[2] flex flex-col gap-1">
            <h1 className="text-h3 font-semibold text-white desktop:text-h1">
              {travelConsultant.name}
            </h1>
            <h2 className="text-h6 font-medium text-white">
              {travelConsultant.jobTitle}
            </h2>
          </div>
          {tidbitsComponents}
          {travelConsultant?.specialties &&
            travelConsultant?.specialties?.length > 0 &&
            specialtiesComponents}
        </div>
      </div>
    </div>
  ) : (
    // DESKTOP BANNER DESIGN
    <div className="relative mt-[3.625rem] w-full overflow-hidden bg-blue-10">
      <div
        className="relative mx-auto flex w-[90rem] flex-row"
        style={backgroundStyle}
      >
        <div className="absolute right-0 -top-10 h-[37.5rem] w-[12.5rem] -rotate-12 bg-gradient-to-l from-blue-10 to-transparent" />
        <div className="absolute right-0 top-0 h-[9.5rem] w-full bg-gradient-to-b from-blue-10 to-transparent" />
        <div className="h-[31.25rem] w-[31.25rem]">
          <ImageCloudinary
            className="z-[0]"
            src={ImageDefault(travelConsultant.image)}
            alt="w"
            width="500"
            height="500"
            layout="responsive"
            objectFit="cover"
            priority={true}
          />
        </div>
        <div className="z-[1] m-12 flex flex-col justify-between gap-4">
          <div>
            <div className="mb-3 h-[1.1875rem]">
              <Breadcrumb />
            </div>
            <h1 className="text-h3 font-semibold desktop:text-h1">
              {travelConsultant.name}
            </h1>
            <h2 className="text-h6 font-medium text-darkgray">
              {travelConsultant.jobTitle}
            </h2>
          </div>
          {tidbitsComponents}
          {specialtiesComponents}
        </div>
      </div>
    </div>
  );
};

export default TCDetailHeader;
